import { Autocomplete, createFilterOptions, TextField, Typography } from '@mui/material';
import { keyframes } from '@mui/system';
import { useRowSelectionContextOptional } from 'components/atoms/RowSelectionContext';
import { Permission } from 'interfaces/permissionOption';
import { find, isEmpty, map, omit, orderBy } from 'lodash';
import React from 'react';
import { useLabs } from 'utils/queryHooks/useLabs';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { useEncodedFilters } from 'utils/useEncodedFilters';
import { usePermissions } from 'utils/usePermissions';

interface Props {
  responsiveWidth?: number;
}

const labNameReveal = keyframes`
  from { clip-path: inset(0 100% 0 0); }
  to { clip-path: inset(0 0 0 0); }
`;

const LabDropdown: React.FunctionComponent<React.PropsWithChildren<Props>> = ({ responsiveWidth = 260 }) => {
  const { data: labsRaw = [] } = useLabs();

  const { hasPermission, isLoading: isLoadingPermissions, isError: isPermissionsError } = usePermissions();
  const canViewMultipleLabs = hasPermission(Permission.ApplyPermissionsAcrossLabs);

  const { labId: currentLabId, userLabId, setLabId } = useCurrentLabId();
  const { setQueryParams, queryParams } = useEncodedFilters();
  const rowSelection = useRowSelectionContextOptional();

  const labs = React.useMemo(() => orderBy(map(labsRaw, rawLabToLab), 'text'), [labsRaw]);
  const selectedLab = find(labs, { value: canViewMultipleLabs ? currentLabId : userLabId }) || null;

  const handleLabClick = (_event: React.SyntheticEvent, lab: Lab | null) => {
    const labId = lab?.value;
    if (!labId) {
      return;
    }

    if (labId !== currentLabId) {
      setLabId(labId, 'replaceIn');
      // Remove studyId filter when switching labs
      setQueryParams({
        page: 1,
        filters:
          queryParams.filters?.studyId !== undefined
            ? isEmpty(omit(queryParams.filters, 'studyId'))
              ? undefined
              : omit(queryParams.filters, 'studyId')
            : queryParams.filters,
        slideIdsToInclude: [],
        caseIdsToInclude: [],
      });
      rowSelection?.clearSelection();
    }
  };

  if (isLoadingPermissions || isPermissionsError) {
    return null;
  }

  if (!canViewMultipleLabs) {
    const characterCount = Math.max(Array.from(selectedLab?.text || '').length, 1);

    return selectedLab ? (
      <Typography
        key={selectedLab.value}
        component="strong"
        fontWeight="bold"
        fontSize="18px"
        sx={{
          display: 'inline-block',
          animationName: `${labNameReveal}`,
          animationDuration: `${Math.min(characterCount * 50, 900)}ms`,
          animationTimingFunction: `steps(${characterCount}, end)`,
          animationIterationCount: 1,
          animationFillMode: 'both',
          '@media (prefers-reduced-motion: reduce)': {
            animation: 'none',
            clipPath: 'none',
          },
        }}
      >
        {selectedLab.text}
      </Typography>
    ) : null;
  }

  return !isEmpty(labs) ? (
    <Autocomplete<Lab, false, true, false>
      disableClearable
      options={labs}
      value={selectedLab}
      onChange={handleLabClick}
      getOptionLabel={(lab) => lab.text}
      filterOptions={(options, state) =>
        filterLabs(options, { ...state, inputValue: normalizeLabSearch(state.inputValue) })
      }
      isOptionEqualToValue={(lab, value) => lab.value === value.value}
      size="small"
      sx={{ width: responsiveWidth, minWidth: Math.min(responsiveWidth, 148), maxWidth: responsiveWidth }}
      renderInput={(params) => <TextField {...params} label="Lab" variant="outlined" />}
    />
  ) : null;
};

interface Lab {
  text: string;
  value: string;
}

const normalizeLabSearch = (value: string) => value.replace(/[\s\p{P}\p{S}]/gu, '');
const filterLabs = createFilterOptions<Lab>({ stringify: (lab) => normalizeLabSearch(lab.text) });

const rawLabToLab = (rawLab: { name: string; id: string }): Lab => ({
  text: rawLab.name,
  value: rawLab.id,
});

export default LabDropdown;
